import { APPConfig, convertToBoolean } from 'config/appConfig'
import { IS_BROWSER } from 'src/utils/application'
import { cartContainer, catalogContainer } from 'src/models'
import { catalogDeps, productDeps } from 'src/deps'
import { trackErrorInInstana } from 'src/utils/commonUtils'

const DEFAULT_QUANTITY = 1
/**
 * The global window contract name exposed to Insider.
 * Namespaced as InsiderAddToCart to avoid collision with generic third-party globals.
 */
const CONTRACT_NAME = 'InsiderAddToCart'
const CONTRACT_PATH = `window.${CONTRACT_NAME}`
const INSIDER_SOURCE = 'insider_contract'

/**
 * Frozen enum of all contract result codes.
 * Frozen to prevent accidental external mutation.
 */
const CONTRACT_CODES = Object.freeze({
  OK: 'OK',
  INVALID_PRODUCT_ID: 'INVALID_PRODUCT_ID',
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
  SKU_RESOLUTION_FAILED: 'SKU_RESOLUTION_FAILED',
  PRODUCT_LOOKUP_FAILED: 'PRODUCT_LOOKUP_FAILED',
  CART_ADD_FAILED: 'CART_ADD_FAILED',
  CART_ADD_EXCEPTION: 'CART_ADD_EXCEPTION',
})

/**
 * Checks whether the Insider AddToCart global contract is enabled
 * via the insider.enableAddToCartGlobalContract config flag.
 * Follows the same pattern as enableInsider in src/utils/insider.js.
 */
function isContractEnabled() {
  const insiderConfig = APPConfig?.getAppConfig?.()?.insider || {}
  return convertToBoolean(insiderConfig?.enableAddToCartGlobalContract)
}

function normalizeProductId(productId) {
  if (typeof productId === 'string') {
    return productId.trim()
  }
  if (typeof productId === 'number') {
    return String(productId)
  }
  return ''
}

function normalizeQuantity(quantity) {
  if (typeof quantity === 'undefined' || quantity === null || quantity === '') {
    return DEFAULT_QUANTITY
  }

  const parsedQuantity = Number(quantity)
  if (!Number.isInteger(parsedQuantity) || parsedQuantity <= 0) {
    return null
  }
  return parsedQuantity
}

function toStringMessage(message = '', fallback = '') {
  const normalized = typeof message === 'string' ? message.trim() : ''
  return normalized || fallback
}

function buildFailureResult({
  code = CONTRACT_CODES.CART_ADD_FAILED,
  message = '',
  retryable = false,
  productId = '',
  quantity = DEFAULT_QUANTITY,
}) {
  return {
    success: false,
    code,
    message,
    retryable,
    productId,
    quantity,
  }
}

function buildSuccessResult({
  productId = '',
  quantity = DEFAULT_QUANTITY,
  cartCount = 0,
}) {
  return {
    success: true,
    code: CONTRACT_CODES.OK,
    productId,
    quantity,
    cartCount,
  }
}

/**
 * Sends a structured telemetry event via trackErrorInInstana.
 * trackErrorInInstana is reused for all event types (not just errors) as it
 * is the project's unified Instana reporting utility. The errorReport field
 * carries the event name; Instana dashboards filter by source='insider_contract'.
 */
function logTelemetry(eventName = '', payload = {}) {
  trackErrorInInstana({
    errorReport: eventName,
    errorData: {
      source: INSIDER_SOURCE,
      ...payload,
    },
  })
}

function getRegularCartItem({
  product = {},
  productId = '',
  quantity = DEFAULT_QUANTITY,
  categoryId = '',
  listName = '',
}) {
  const selectedSku =
    catalogDeps?.getDefaultSelectedSku?.(product)?.[0] || product?.sku?.[0] || {}
  const skuId = selectedSku?.identifier || ''

  if (!skuId) {
    return null
  }

  return {
    quantity,
    skuId,
    productId: product?.identifier || productId,
    categoryId,
    listName,
    slug: product?.properties?.slug || '',
    isBackOrdered: selectedSku?.inventoryProperties?.backOrdered || false,
  }
}

function getBundleCartItem({
  product = {},
  productId = '',
  quantity = DEFAULT_QUANTITY,
  categoryId = '',
  listName = '',
}) {
  const normalizedBundleProduct = {
    ...product,
    productId: product?.identifier || productId,
    categoryId,
  }

  const bundleData = productDeps?.getBundleData?.(normalizedBundleProduct, {
    compareAndSetBackorderBasedOnQuantity: true,
    currentlySelectedQuantity: quantity,
  })

  if (!bundleData?.skus?.length) {
    return null
  }

  return {
    quantity,
    categoryId,
    listName,
    slug: product?.properties?.slug || '',
    isBundle: true,
    brand: product?.brand || product?.properties?.brand || '',
    imageURL: product?.imageURL || product?.properties?.imageURL || '',
    name: product?.name || product?.properties?.name || '',
    productId: product?.identifier || productId,
    isPbomProduct: productDeps?.isPbomProduct?.(normalizedBundleProduct) || false,
    ...bundleData,
  }
}

async function resolveCartArray({
  productId = '',
  quantity = DEFAULT_QUANTITY,
}) {
  let productResponse = {}
  try {
    productResponse = await catalogContainer?.getProduct?.({
      productId,
      suppressProductEvent: true,
    })
  } catch (error) {
    return {
      error: buildFailureResult({
        code: CONTRACT_CODES.PRODUCT_LOOKUP_FAILED,
        message: toStringMessage(
          error?.message,
          'Unable to fetch product details for add to cart'
        ),
        retryable: true,
        productId,
        quantity,
      }),
    }
  }
  const product = productResponse?.product?.[0] || {}

  if (!product?.identifier) {
    return {
      error: buildFailureResult({
        code: CONTRACT_CODES.PRODUCT_NOT_FOUND,
        message: 'Product not found or not sellable',
        retryable: false,
        productId,
        quantity,
      }),
    }
  }

  // Use identifier (stable cart key) before name (display label) for categoryId.
  const categoryId =
    productResponse?.category?.identifier || productResponse?.category?.name || ''
  const listName = productResponse?.category?.parentId || productResponse?.searchTerm || ''
  const isBundleProduct = productDeps?.isBundleData?.(product)
  const cartItem = isBundleProduct
    ? getBundleCartItem({ product, productId, quantity, categoryId, listName })
    : getRegularCartItem({ product, productId, quantity, categoryId, listName })

  if (!cartItem) {
    return {
      error: buildFailureResult({
        code: CONTRACT_CODES.SKU_RESOLUTION_FAILED,
        message: 'Unable to resolve an addable SKU for this product',
        retryable: false,
        productId,
        quantity,
      }),
    }
  }

  return {
    cartArray: [cartItem],
    resolvedProductId: product?.identifier || productId,
  }
}

async function invokeContract(productId, quantity = DEFAULT_QUANTITY) {
  const normalizedProductId = normalizeProductId(productId)
  const normalizedQuantity = normalizeQuantity(quantity)

  if (!normalizedProductId) {
    const invalidProductResult = buildFailureResult({
      code: CONTRACT_CODES.INVALID_PRODUCT_ID,
      message: 'Product id is required',
      retryable: false,
      productId: '',
      quantity: normalizedQuantity || DEFAULT_QUANTITY,
    })
    logTelemetry('Insider AddToCart Validation Failed', invalidProductResult)
    return invalidProductResult
  }

  if (!normalizedQuantity) {
    const invalidQuantityResult = buildFailureResult({
      code: CONTRACT_CODES.INVALID_QUANTITY,
      message: 'Quantity must be a positive integer',
      retryable: false,
      productId: normalizedProductId,
      quantity,
    })
    logTelemetry('Insider AddToCart Validation Failed', invalidQuantityResult)
    return invalidQuantityResult
  }

  try {
    const { cartArray, error, resolvedProductId } = await resolveCartArray({
      productId: normalizedProductId,
      quantity: normalizedQuantity,
    })

    if (error) {
      logTelemetry('Insider AddToCart SKU Resolution Failed', error)
      return error
    }

    const cartResponse = await cartContainer?.addToCart?.({
      cartArray,
      isShowToast: false,
    })
    const isSuccess = cartContainer?.isSuccessResponse?.(cartResponse)

    if (!isSuccess) {
      const addToCartError = buildFailureResult({
        code: cartResponse?.code || CONTRACT_CODES.CART_ADD_FAILED,
        message: toStringMessage(
          cartResponse?.message,
          'Failed to add item to cart'
        ),
        retryable: false,
        productId: resolvedProductId || normalizedProductId,
        quantity: normalizedQuantity,
      })
      logTelemetry('Insider AddToCart API Failed', {
        ...addToCartError,
        apiCode: cartResponse?.code || '',
      })
      return addToCartError
    }

    const successResult = buildSuccessResult({
      productId: resolvedProductId || normalizedProductId,
      quantity: normalizedQuantity,
      cartCount: cartContainer?.cartCount || 0,
    })
    logTelemetry('Insider AddToCart Success', successResult)
    return successResult
  } catch (error) {
    const failureResult = buildFailureResult({
      code: CONTRACT_CODES.CART_ADD_EXCEPTION,
      message: toStringMessage(
        error?.message,
        'Unexpected error while adding item to cart'
      ),
      retryable: true,
      productId: normalizedProductId,
      quantity: normalizedQuantity,
    })
    logTelemetry('Insider AddToCart Exception', {
      ...failureResult,
      errorName: error?.name || '',
    })
    return failureResult
  }
}

function removeContractFromWindow() {
  if (!IS_BROWSER) {
    return
  }
  if (Object.prototype.hasOwnProperty.call(window, CONTRACT_NAME)) {
    delete window[CONTRACT_NAME]
  }
}

function registerInsiderAddToCartGlobal() {
  if (!IS_BROWSER) {
    return
  }

  if (!isContractEnabled()) {
    removeContractFromWindow()
    return
  }

  // Guard against double-registration: only assign and log if not already set.
  if (Object.prototype.hasOwnProperty.call(window, CONTRACT_NAME)) {
    return
  }

  window[CONTRACT_NAME] = (productId, quantity) =>
    invokeContract(productId, quantity)

  logTelemetry('Insider AddToCart Contract Registered', {
    contract: CONTRACT_PATH,
  })
}

export { registerInsiderAddToCartGlobal, invokeContract, CONTRACT_CODES }
